/**
 * Config de navegación del NeoCore (ADR 014 · shell shadcn). Portado 1:1 del
 * AppShell viejo: mismas rutas, secciones, gating por módulo y badges. Íconos
 * migrados a lucide (outline, como el brand). Solo cambia la presentación.
 */
import {
  LayoutDashboard, LineChart, UserMinus, Users, HandCoins, PiggyBank, Receipt,
  ShoppingBag, Building2, Banknote, Landmark, Vault, Calculator, MessageCircle,
  PhoneCall, Library, Coins, FileText, Filter, Share2, ShieldCheck, Fingerprint,
  Settings, UserCog, AlertTriangle, UserPlus, Radio, HandHeart, Bell, Wallet, ClipboardCheck, ScanFace, ShieldAlert, type LucideIcon,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { ModuleKey } from '@/lib/useRole'

export type BadgeTone = 'warn' | 'accent' | 'neutral'
export type NavItem = {
  to: string
  label: string
  Icon: LucideIcon
  section?: string
  badge?: () => Promise<number>
  badgeTone?: BadgeTone
  module?: ModuleKey
}

// ── Badge fetchers (idénticos al AppShell viejo) ──────────────────────────────
async function fetchBajasPendientes(): Promise<number> {
  const { count } = await supabase.from('solicitud_baja').select('*', { count: 'exact', head: true }).eq('estado', 'pendiente')
  return count ?? 0
}
async function fetchProspectosPendientes(): Promise<number> {
  const { count } = await supabase.from('asociado').select('*', { count: 'exact', head: true }).in('estado_asociado', ['prospecto', 'pendiente'])
  return count ?? 0
}
async function fetchRetirosPendientes(): Promise<number> {
  const { count } = await supabase.from('retiro').select('*', { count: 'exact', head: true }).eq('estado', 'solicitado')
  return count ?? 0
}
async function fetchCobranzaVencidas(): Promise<number> {
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })
  const { count } = await supabase.from('credito_cuota').select('*', { count: 'exact', head: true }).eq('estado', 'pendiente').lt('fecha_vencimiento', hoy)
  return count ?? 0
}
async function fetchOriginacionAccionRequerida(): Promise<number> {
  const pend = await supabase.from('solicitud_credito').select('*', { count: 'exact', head: true }).eq('estado', 'pendiente')
  const firmadas = await supabase.from('solicitud_credito').select('*', { count: 'exact', head: true }).eq('estado', 'aprobada').not('firma_timestamp', 'is', null)
  return (pend.count ?? 0) + (firmadas.count ?? 0)
}

// Orden por uso (Hugo, 2026-10): Hoy · Asociados · Créditos · Ahorros y Plazos ·
// Analítica · Tesorería · Cumplimiento · Contabilidad · Administración · Sistema.
// "Hoy" son ATAJOS a las colas con pendiente (badges); cada ítem tiene además su
// hogar canónico en su dominio (duplicación deliberada, no azarosa).
export const NAV: NavItem[] = [
  // ── Hoy · bandeja del día (atajos a colas con acción pendiente) ──────────────
  { to: '/neocore/reportes-pago', label: 'Reportes de pago', Icon: Receipt, section: 'Hoy', module: 'caja' },
  { to: '/neocore/creditos/originacion', label: 'Originación', Icon: HandCoins, section: 'Hoy', module: 'creditos', badge: fetchOriginacionAccionRequerida, badgeTone: 'warn' },
  { to: '/neocore/cobranza', label: 'Cobranza', Icon: PhoneCall, section: 'Hoy', module: 'cobranza', badge: fetchCobranzaVencidas, badgeTone: 'warn' },
  { to: '/neocore/retiros', label: 'Retiros', Icon: Wallet, section: 'Hoy', module: 'bancos', badge: fetchRetirosPendientes, badgeTone: 'warn' },
  { to: '/neocore/asociar/nuevo', label: 'Inscribir', Icon: UserPlus, section: 'Hoy', module: 'asociados' },
  { to: '/neocore/verificaciones-bancarias', label: 'Verificación de cuentas', Icon: ShieldCheck, section: 'Hoy', module: 'kyc' },
  { to: '/bajas', label: 'Bajas de cuenta', Icon: UserMinus, section: 'Hoy', module: 'solicitudes_baja', badge: fetchBajasPendientes, badgeTone: 'warn' },

  // ── Asociados ────────────────────────────────────────────────────────────────
  { to: '/neocore/asociados', label: 'Asociados', Icon: Users, section: 'Asociados', module: 'asociados', badge: fetchProspectosPendientes, badgeTone: 'warn' },
  { to: '/neocore/asociar/nuevo', label: 'Inscribir', Icon: UserPlus, section: 'Asociados', module: 'asociados' },
  { to: '/neocore/credenciales-app', label: 'Credenciales del app', Icon: Fingerprint, section: 'Asociados', module: 'credenciales' },
  { to: '/neocore/datos-sat', label: 'Datos SAT · NIT', Icon: FileText, section: 'Asociados', module: 'asociados' },
  { to: '/bajas', label: 'Bajas de cuenta', Icon: UserMinus, section: 'Asociados', module: 'solicitudes_baja', badge: fetchBajasPendientes, badgeTone: 'warn' },

  // ── Créditos (ciclo completo) ──────────────────────────────────────────────────
  { to: '/neocore/creditos', label: 'Créditos', Icon: HandCoins, section: 'Créditos', module: 'creditos' },
  { to: '/neocore/creditos/originacion', label: 'Originación', Icon: HandCoins, section: 'Créditos', module: 'creditos', badge: fetchOriginacionAccionRequerida, badgeTone: 'warn' },
  { to: '/neocore/cobranza', label: 'Cobranza', Icon: PhoneCall, section: 'Créditos', module: 'cobranza', badge: fetchCobranzaVencidas, badgeTone: 'warn' },
  { to: '/neocore/cobranza-cockpit', label: 'Cobranza · cockpit', Icon: PhoneCall, section: 'Créditos', module: 'cobranza' },
  { to: '/neocore/test-cobranza', label: 'Test de cobranza', Icon: PhoneCall, section: 'Hoy', module: 'cobranza' },
  { to: '/neocore/campana-inscripcion', label: 'Campaña inscripción', Icon: MessageCircle, section: 'Hoy', module: 'inscripciones' },
  { to: '/neocore/cotizador', label: 'Cotizador', Icon: Calculator, section: 'Créditos', module: 'creditos' },
  { to: '/neocore/invitar-credito', label: 'Invitar a crédito', Icon: MessageCircle, section: 'Créditos', module: 'creditos' },
  { to: '/neocore/presa-crediticia', label: 'Presa crediticia', Icon: Filter, section: 'Créditos', module: 'creditos' },
  { to: '/neocore/condonaciones-mora', label: 'Condonaciones de mora', Icon: HandHeart, section: 'Créditos', module: 'creditos' },
  { to: '/neocore/referidos', label: 'Hunter', Icon: Share2, section: 'Créditos', module: 'creditos' },
  { to: '/neocore/creditos/bi', label: 'BI créditos', Icon: LineChart, section: 'Créditos', module: 'creditos' },
  { to: '/neocore/creditos/bi-momentum', label: 'BI · Momentum', Icon: LineChart, section: 'Créditos', module: 'creditos' },

  // ── Ahorros y Plazos ───────────────────────────────────────────────────────────
  { to: '/neocore/ahorros', label: 'Ahorros', Icon: PiggyBank, section: 'Ahorros y Plazos' },
  { to: '/neocore/plazos-fijos', label: 'Plazos fijos', Icon: Vault, section: 'Ahorros y Plazos', module: 'plazos_fijos' },

  // ── Analítica ───────────────────────────────────────────────────────────────────
  { to: '/', label: 'Panel', Icon: LayoutDashboard, section: 'Analítica' },
  { to: '/neocore/panel-vivo', label: 'Panel vivo', Icon: Radio, section: 'Analítica', module: 'creditos' },
  { to: '/neocore/actividad', label: 'Actividad app', Icon: Radio, section: 'Analítica' },
  { to: '/neocore/reportes', label: 'Reportes', Icon: LineChart, section: 'Analítica' },

  // ── Tesorería ───────────────────────────────────────────────────────────────────
  { to: '/neocore/caja', label: 'Caja', Icon: Banknote, section: 'Tesorería', module: 'caja' },
  { to: '/neocore/bancos', label: 'Bancos', Icon: Landmark, section: 'Tesorería', module: 'bancos' },
  { to: '/neocore/movimientos-caja', label: 'Movimientos de caja', Icon: Coins, section: 'Tesorería', module: 'contabilidad' },
  { to: '/neocore/conciliacion-bancaria', label: 'Conciliación bancaria', Icon: Landmark, section: 'Tesorería', module: 'contabilidad' },
  { to: '/neocore/conciliacion-onetap', label: 'Conciliación', Icon: ClipboardCheck, section: 'Tesorería', module: 'contabilidad' },
  { to: '/neocore/retiros', label: 'Retiros', Icon: Wallet, section: 'Tesorería', module: 'bancos', badge: fetchRetirosPendientes, badgeTone: 'warn' },

  // ── Cumplimiento ─────────────────────────────────────────────────────────────────
  { to: '/neocore/cumplimiento/verificaciones-analista', label: 'Verificaciones de analista', Icon: Fingerprint, section: 'Cumplimiento', module: 'kyc' },
  { to: '/neocore/cumplimiento/okid-verificaciones', label: 'Verificaciones OKID', Icon: ScanFace, section: 'Cumplimiento', module: 'kyc' },
  { to: '/neocore/cumplimiento/casos-aml', label: 'Casos AML', Icon: ShieldAlert, section: 'Cumplimiento', module: 'kyc' },
  { to: '/neocore/cumplimiento/okid-config', label: 'OkID · Configuración', Icon: ScanFace, section: 'Cumplimiento', module: 'kyc' },
  { to: '/neocore/cumplimiento/okid-test', label: 'OKID · test', Icon: ScanFace, section: 'Cumplimiento', module: 'kyc' },
  { to: '/neocore/verificaciones-bancarias', label: 'Verificaciones bancarias', Icon: ShieldCheck, section: 'Cumplimiento', module: 'kyc' },
  { to: '/neocore/cumplimiento/bitacora-ofac', label: 'Bitácora OFAC', Icon: ShieldCheck, section: 'Cumplimiento', module: 'kyc' },

  // ── Contabilidad ──────────────────────────────────────────────────────────────────
  { to: '/neocore/contabilidad', label: 'Balance y asientos', Icon: Calculator, section: 'Contabilidad', module: 'contabilidad' },
  { to: '/neocore/conciliacion-mensual', label: 'Conciliación mensual (banco vs sistema)', Icon: Landmark, section: 'Contabilidad', module: 'contabilidad' },
  { to: '/neocore/catalogo-contable', label: 'Catálogo contable', Icon: Library, section: 'Contabilidad', module: 'contabilidad' },
  { to: '/neocore/contabilidad/reportes', label: 'Reportes INGECOP', Icon: Library, section: 'Contabilidad', module: 'contabilidad' },
  { to: '/neocore/reporte-intereses', label: 'Intereses e ISR', Icon: Coins, section: 'Contabilidad', module: 'contabilidad' },
  { to: '/neocore/carteras', label: 'Carteras', Icon: LineChart, section: 'Contabilidad', module: 'contabilidad' },

  // ── Administración (back-office) ──────────────────────────────────────────────────
  { to: '/neocore/gastos', label: 'Gastos', Icon: ShoppingBag, section: 'Administración', module: 'gastos' },
  { to: '/neocore/planillas', label: 'Planillas', Icon: Wallet, section: 'Administración', module: 'contabilidad' },
  { to: '/neocore/proveedores', label: 'Proveedores', Icon: Building2, section: 'Administración', module: 'proveedores' },

  // ── Sistema ────────────────────────────────────────────────────────────────────────
  { to: '/neocore/usuarios', label: 'Usuarios staff', Icon: UserCog, section: 'Sistema', module: 'usuarios_staff' },
  { to: '/neocore/notificaciones', label: 'Notificaciones', Icon: Bell, section: 'Sistema' },
  { to: '/neocore/parametros', label: 'Parámetros', Icon: Settings, section: 'Sistema' },
  { to: '/neocore/config-reporte', label: 'Encabezado reportes', Icon: FileText, section: 'Sistema', module: 'contabilidad' },
  { to: '/neocore/errores', label: 'Errores', Icon: AlertTriangle, section: 'Sistema' },
  { to: '/neocore/migracion-onboarding', label: 'Migración COBASY', Icon: Fingerprint, section: 'Sistema', module: 'migracion' },
]

export const TITLE_BY_PATH: Record<string, string> = {
  '/': 'Panel',
  '/neocore/asociados': 'Asociados',
  '/neocore/asociar/nuevo': 'Inscribir asociado',
  '/neocore/credenciales-app': 'Credenciales del app móvil',
  '/neocore/migracion-onboarding': 'Migración onboarding COBASY',
  '/bajas': 'Bajas de cuenta',
  '/neocore/creditos': 'Créditos',
  '/neocore/cobranza': 'Cobranza',
  '/neocore/cobranza-cockpit': 'Cobranza · cockpit del día',
  '/neocore/test-cobranza': 'Test de cobranza · octubre',
  '/neocore/campana-inscripcion': 'Campaña inscripción · Q100',
  '/neocore/conciliacion-mensual': 'Conciliación mensual',
  '/neocore/creditos/originacion': 'Originación de crédito',
  '/neocore/creditos/bi': 'BI créditos',
  '/neocore/creditos/bi-momentum': 'BI créditos · Momentum',
  '/neocore/panel-vivo': 'Panel vivo',
  '/neocore/cotizador': 'Cotizador de créditos',
  '/neocore/invitar-credito': 'Invitar a crédito',
  '/neocore/presa-crediticia': 'Presa crediticia',
  '/neocore/referidos': 'Hunter',
  '/neocore/condonaciones-mora': 'Condonaciones de mora',
  '/neocore/plazos-fijos': 'Plazos fijos',
  '/neocore/ahorros': 'Ahorros y aportaciones',
  '/neocore/caja': 'Caja',
  '/neocore/bancos': 'Bancos',
  '/neocore/reportes-pago': 'Reportes de pago',
  '/neocore/actividad': 'Actividad de la app',
  '/neocore/reportes': 'Reportes',
  '/neocore/errores': 'Errores del sistema',
  '/neocore/notificaciones': 'Centro de notificaciones',
  '/neocore/planillas': 'Planillas',
  '/neocore/verificaciones-bancarias': 'Verificaciones bancarias',
  '/neocore/retiros': 'Retiros',
  '/neocore/contabilidad': 'Balance y asientos',
  '/neocore/catalogo-contable': 'Catálogo contable INGECOP',
  '/neocore/contabilidad/reportes': 'Reportes contables INGECOP',
  '/neocore/movimientos-caja': 'Movimientos de caja',
  '/neocore/carteras': 'Carteras pasiva y activa',
  '/neocore/conciliacion-bancaria': 'Conciliación bancaria',
  '/neocore/conciliacion-onetap': 'Conciliación',
  '/neocore/reporte-intereses': 'Intereses pagados y retenciones ISR',
  '/neocore/datos-sat': 'Datos SAT · NIT y fecha de nacimiento',
  '/neocore/config-reporte': 'Encabezado y pie de reportes',
  '/neocore/gastos': 'Gastos',
  '/neocore/proveedores': 'Proveedores',
  '/neocore/cumplimiento/verificaciones-analista': 'Verificaciones de analista',
  '/neocore/cumplimiento/okid-verificaciones': 'Verificaciones de identidad · OKID',
  '/neocore/cumplimiento/casos-aml': 'Casos AML',
  '/neocore/cumplimiento/okid-config': 'OkID · Configuración',
  '/neocore/cumplimiento/okid-test': 'OKID · verificación de prueba',
  '/neocore/cumplimiento/bitacora-ofac': 'Bitácora OFAC',
  '/neocore/usuarios': 'Usuarios staff',
  '/neocore/parametros': 'Parámetros',
}
